// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/billing/FxRatesPage.tsx
//
// Phase 3.2 Task 34 — Admin FX Rates page (table grouped by gateway).
//
// Per CONTEXT.md (rate_micro = rate × 10^6, HALF_EVEN; admin-entered
// for Tier 2 + display estimate for Tier 1; stale toggle hides currency from
// merchant topup form). Covers goal G-FX-08.
//
// Endpoints (Per API-CONTRACTS.md L2056-2173):
//   GET    /api/v1/admin/fx-rates
//   PUT    /api/v1/admin/fx-rates/:gateway/:currency
//   DELETE /api/v1/admin/fx-rates/:gateway/:currency
//
// Design: admin-fx-rates-table.png — table grouped by gateway as columns,
// currency rows with symbol prefix (€ EUR / £ GBP / đ VND / ¥ JPY), inline
// editable rate cells, "Last updated" column with green/red dot indicator,
// "Add Currency" CTA top-right. Stale rows: opacity-60 + red label.

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import {
  envelopeError,
  newIdempotencyKey,
  unwrap,
  type LooseClient,
} from './billing-shared.js';
import { useTranslation } from '../../i18n/useTranslation.js';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

interface FxRateRow {
  gateway: string;
  currency: string;
  rate_micro: number;
  updated_at: string;
  is_stale: boolean;
}

// Currency display symbol prefix (matches design: € / £ / đ / ¥ / etc.)
// Local — phase-specific table-cell decorator, not promoted to utils.
const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  VND: 'đ',
  JPY: '¥',
  CNY: '¥',
  KRW: '₩',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
  THB: '฿',
};

const STALE_DAYS = 30; // Per design: "Stale ≥30 days flagged red"

/**
 * Format rate_micro (integer × 10^6) → 6-decimal display string.
 * Example: 1080000 → "1.080000"; 24500000000 → "24500.000000".
 * Local helper — phase-specific FX display rule, tightly coupled
 * to the rate_micro storage convention; not generic enough for utils.
 */
function formatRateMicro(rateMicro: number): string {
  return (rateMicro / 1_000_000).toFixed(6);
}

/** Parse user input (e.g., "1.080000") → integer rate_micro. */
function parseRateMicro(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 1_000_000);
}

function relativeAge(iso: string, t: TFn): { days: number; label: string } {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / (24 * 3600 * 1000));
  if (days <= 0) return { days, label: t('fx_rate.age.today') };
  if (days === 1) return { days, label: t('fx_rate.age.oneDayAgo') };
  return { days, label: t('fx_rate.age.daysAgo', { days }) };
}

export function FxRatesPage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const qc = useQueryClient();

  const ratesQuery = useQuery({
    queryKey: ['admin', 'fx-rates'],
    queryFn: async (): Promise<FxRateRow[]> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.GET('/api/v1/admin/fx-rates');
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? t('errors.requestFailed', { status: response.status }));
      }
      // Issue 9 fix (debug-bill iter 14, 2026-05-04): BE shape is
      // `{success:true, data:[...rates]}` — unwrap() already extracts the
      // inner `data` (an array). Reaching `.data` on the array returned
      // undefined, hiding all FX rate rows + gateway columns.
      return unwrap<FxRateRow[]>(data) ?? [];
    },
  });

  // ---- Allowed Currencies ----
  const allowedQuery = useQuery({
    queryKey: ['admin', 'allowed-currencies'],
    queryFn: async (): Promise<string[]> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.GET(
        '/api/v1/admin/billing-settings/currencies',
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? t('errors.requestFailed', { status: response.status }));
      }
      const payload = unwrap<{ allowed: string[] }>(data);
      return payload.allowed ?? [];
    },
  });

  const [allowedDraft, setAllowedDraft] = useState<string[]>([]);
  const [newAllowedCode, setNewAllowedCode] = useState('');

  useEffect(() => {
    if (allowedQuery.data) setAllowedDraft(allowedQuery.data);
  }, [allowedQuery.data]);

  const allowedDirty = useMemo(() => {
    const a = (allowedQuery.data ?? []).slice().sort().join(',');
    const b = allowedDraft.slice().sort().join(',');
    return a !== b;
  }, [allowedQuery.data, allowedDraft]);

  function addAllowedCurrency(): void {
    const code = newAllowedCode.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) {
      toast({
        variant: 'error',
        title: t('fx_rate.toast.isoInvalid'),
      });
      return;
    }
    if (allowedDraft.includes(code)) {
      toast({
        variant: 'error',
        title: t('fx_rate.toast.alreadyInList', { code }),
      });
      return;
    }
    setAllowedDraft((prev) => [...prev, code]);
    setNewAllowedCode('');
  }

  function removeAllowedCurrency(code: string): void {
    setAllowedDraft((prev) => prev.filter((c) => c !== code));
  }

  const allowedMutation = useMutation({
    mutationFn: async (allowed: string[]): Promise<string[]> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.PUT(
        '/api/v1/admin/billing-settings/currencies',
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body: { allowed },
        },
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? t('errors.requestFailed', { status: response.status }));
      }
      const payload = unwrap<{ allowed: string[] }>(data);
      return payload.allowed ?? [];
    },
    onSuccess: (saved) => {
      qc.setQueryData(['admin', 'allowed-currencies'], saved);
      setAllowedDraft(saved);
      toast({ variant: 'success', title: t('fx_rate.toast.allowedSaved') });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('fx_rate.toast.allowedSaveFailed'),
        description:
          err instanceof Error ? err.message : t('fx_rate.toast.networkError'),
      });
    },
  });

  // Pivot: { gateway: { currency: row } } and unique gateway/currency lists.
  // Bug 12 (debug-bill iter 6, 2026-05-04): also include currencies from
  // the allowed-currencies whitelist so adding a new currency via the top
  // Save button immediately renders an empty column the operator can fill in.
  // Previously columns only came from rate rows, so newly-allowed currencies
  // appeared invisible until a row was created elsewhere.
  const { gateways, currencies, byKey } = useMemo(() => {
    const gws = new Set<string>();
    const ccys = new Set<string>();
    const map = new Map<string, FxRateRow>();
    for (const row of ratesQuery.data ?? []) {
      gws.add(row.gateway);
      ccys.add(row.currency);
      map.set(`${row.gateway}::${row.currency}`, row);
    }
    for (const c of allowedDraft) {
      ccys.add(c);
    }
    return {
      gateways: Array.from(gws).sort(),
      currencies: Array.from(ccys).sort(),
      byKey: map,
    };
  }, [ratesQuery.data, allowedDraft]);

  // Inline edit state — track which (gateway, currency) cell is open.
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const putMutation = useMutation({
    mutationFn: async (params: {
      gateway: string;
      currency: string;
      rate_micro: number;
    }): Promise<FxRateRow> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.PUT(
        `/api/v1/admin/fx-rates/${encodeURIComponent(params.gateway)}/${encodeURIComponent(params.currency)}`,
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body: { rate_micro: params.rate_micro },
        },
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? t('errors.requestFailed', { status: response.status }));
      }
      return unwrap<FxRateRow>(data);
    },
    onSuccess: () => {
      toast({ variant: 'success', title: t('fx_rate.toast.rateSaved') });
      void qc.invalidateQueries({ queryKey: ['admin', 'fx-rates'] });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('fx_rate.toast.rateSaveFailed'),
        description: err instanceof Error ? err.message : t('fx_rate.toast.networkError'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (params: { gateway: string; currency: string }): Promise<void> => {
      const c = apiClient() as unknown as LooseClient;
      const { error, response } = await c.DELETE(
        `/api/v1/admin/fx-rates/${encodeURIComponent(params.gateway)}/${encodeURIComponent(params.currency)}`,
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? t('errors.requestFailed', { status: response.status }));
      }
    },
    onSuccess: () => {
      toast({ variant: 'success', title: t('fx_rate.toast.markedStale') });
      void qc.invalidateQueries({ queryKey: ['admin', 'fx-rates'] });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('fx_rate.toast.markStaleFailed'),
        description: err instanceof Error ? err.message : t('fx_rate.toast.networkError'),
      });
    },
  });

  function commitEdit(gateway: string, currency: string): void {
    const next = parseRateMicro(editValue);
    if (next === null) {
      toast({
        variant: 'error',
        title: t('fx_rate.toast.invalidRate'),
        description: t('fx_rate.toast.invalidRateDesc'),
      });
      return;
    }
    putMutation.mutate({ gateway, currency, rate_micro: next });
    setEditKey(null);
    setEditValue('');
  }

  // ---- Add currency modal ----
  const [addOpen, setAddOpen] = useState(false);
  const [addCurrency, setAddCurrency] = useState('');
  const [addGateway, setAddGateway] = useState('');
  const [addRate, setAddRate] = useState('');

  function submitAdd(): void {
    const code = addCurrency.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) {
      toast({ variant: 'error', title: t('fx_rate.toast.isoInvalidShort') });
      return;
    }
    if (!addGateway.trim()) {
      toast({ variant: 'error', title: t('fx_rate.toast.gatewayRequired') });
      return;
    }
    const rate = parseRateMicro(addRate);
    if (rate === null) {
      toast({ variant: 'error', title: t('fx_rate.toast.invalidRate') });
      return;
    }
    putMutation.mutate({ gateway: addGateway.trim(), currency: code, rate_micro: rate });
    setAddOpen(false);
    setAddCurrency('');
    setAddGateway('');
    setAddRate('');
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8">
      <PageHeader
        title={t('fx_rate.page.heading')}
        subtitle={t('fx_rate.page.subtitle', { days: STALE_DAYS })}
        actions={
          <Button onClick={() => setAddOpen(true)} data-testid="fx-add-currency">
            {t('fx_rate.addCurrency')}
          </Button>
        }
      />

      {/* ---- Allowed Currencies ---- */}
      <section
        className="rounded-xl border border-pw-border bg-pw-bg p-6"
        data-testid="allowed-currencies-section"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-pw-text">
              {t('fx_rate.allowed.heading')}
            </h2>
            <p className="mt-1 text-sm text-pw-text-muted">
              {t('fx_rate.allowed.description')}
            </p>
          </div>
          <Button
            onClick={() => allowedMutation.mutate(allowedDraft)}
            disabled={
              !allowedDirty ||
              allowedMutation.isPending ||
              allowedDraft.length === 0
            }
            data-testid="allowed-currencies-save"
          >
            {allowedMutation.isPending ? t('fx_rate.allowed.saving') : t('fx_rate.allowed.save')}
          </Button>
        </div>

        {allowedQuery.isLoading ? (
          <p className="mt-4 text-sm text-pw-text-muted">{t('fx_rate.allowed.loading')}</p>
        ) : allowedQuery.isError ? (
          <p className="mt-4 text-sm text-red-600 dark:text-red-300">
            {allowedQuery.error instanceof Error
              ? allowedQuery.error.message
              : t('fx_rate.failedToLoad')}
          </p>
        ) : (
          <>
            <div
              className="mt-4 flex flex-wrap gap-2"
              data-testid="allowed-currencies-list"
            >
              {allowedDraft.length === 0 ? (
                <p className="text-sm text-pw-text-muted">
                  {t('fx_rate.allowed.empty')}
                </p>
              ) : (
                allowedDraft.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center gap-1.5 rounded-full bg-pw-primary/10 px-3 py-1 text-xs font-semibold text-pw-primary-strong"
                    data-testid={`allowed-chip-${code}`}
                  >
                    {code}
                    <button
                      type="button"
                      className="rounded-full text-pw-primary-strong/70 hover:text-red-600"
                      onClick={() => removeAllowedCurrency(code)}
                      aria-label={t('fx_rate.allowed.removeAria', { code })}
                      data-testid={`allowed-remove-${code}`}
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="allowed-new-code"
                  className="text-xs font-medium text-pw-text-muted"
                >
                  {t('fx_rate.allowed.addLabel')}
                </label>
                <Input
                  id="allowed-new-code"
                  value={newAllowedCode}
                  maxLength={3}
                  onChange={(e) => setNewAllowedCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addAllowedCurrency();
                  }}
                  placeholder="EUR"
                  className="w-28"
                  data-testid="allowed-new-input"
                />
              </div>
              <Button
                variant="secondary"
                onClick={addAllowedCurrency}
                disabled={newAllowedCode.trim().length === 0}
                data-testid="allowed-add-btn"
              >
                {t('fx_rate.allowed.add')}
              </Button>
              {allowedDirty ? (
                <p className="ml-2 text-xs text-pw-text-muted">
                  {t('fx_rate.allowed.unsavedHint')}
                </p>
              ) : null}
            </div>
          </>
        )}
      </section>

      {/* ---- Table card ---- */}
      <section className="overflow-x-auto rounded-xl border border-pw-border bg-pw-bg">
        {ratesQuery.isLoading ? (
          <p className="p-6 text-sm text-pw-text-muted">{t('fx_rate.table.loading')}</p>
        ) : ratesQuery.isError ? (
          <p className="p-6 text-sm text-red-600 dark:text-red-300">
            {ratesQuery.error instanceof Error ? ratesQuery.error.message : t('fx_rate.failedToLoad')}
          </p>
        ) : currencies.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-12">
            <p className="text-sm text-pw-text-muted">{t('fx_rate.table.empty')}</p>
            <Button onClick={() => setAddOpen(true)}>{t('fx_rate.addCurrency')}</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-pw-primary/5 text-left">
                <th className="px-4 py-3 font-medium text-pw-text-muted">{t('fx_rate.table.colCurrency')}</th>
                {gateways.map((gw) => (
                  <th key={gw} className="px-4 py-3 font-medium capitalize text-pw-text-muted">
                    {gw}
                  </th>
                ))}
                <th className="px-4 py-3 font-medium text-pw-text-muted">{t('fx_rate.table.colLastUpdated')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {currencies.map((currency) => {
                // Use newest row across gateways for the "last updated" column
                // (and stale flag — if any non-stale row exists for this
                // currency, surface the freshest one's timestamp).
                const rowsForCurrency = gateways
                  .map((gw) => byKey.get(`${gw}::${currency}`))
                  .filter((r): r is FxRateRow => Boolean(r));
                const freshest = rowsForCurrency.reduce<FxRateRow | null>(
                  (acc, r) =>
                    !acc || new Date(r.updated_at).getTime() > new Date(acc.updated_at).getTime()
                      ? r
                      : acc,
                  null,
                );
                const stale = freshest ? freshest.is_stale : true;
                const age = freshest ? relativeAge(freshest.updated_at, t) : { days: 0, label: '—' };

                return (
                  <tr
                    key={currency}
                    className={cn(
                      'border-b border-pw-border last:border-0',
                      stale ? 'opacity-60' : '',
                    )}
                  >
                    <td className="px-4 py-3 font-medium text-pw-text">
                      <span className="mr-1 text-pw-text-muted">
                        {CURRENCY_SYMBOL[currency] ?? ''}
                      </span>
                      {currency}
                    </td>
                    {gateways.map((gw) => {
                      const key = `${gw}::${currency}`;
                      const row = byKey.get(key);
                      if (!row) {
                        return (
                          <td
                            key={gw}
                            className="cursor-pointer px-4 py-3 text-pw-text-muted hover:bg-pw-surface"
                            onClick={() => {
                              setEditKey(key);
                              setEditValue('');
                            }}
                            data-testid={`fx-cell-${gw}-${currency}`}
                          >
                            —
                          </td>
                        );
                      }
                      const editing = editKey === key;
                      return (
                        <td
                          key={gw}
                          className={cn(
                            'cursor-pointer px-4 py-3 hover:bg-pw-surface',
                            row.is_stale ? 'text-red-600 dark:text-red-300' : '',
                          )}
                          onClick={() => {
                            if (!editing) {
                              setEditKey(key);
                              setEditValue(formatRateMicro(row.rate_micro));
                            }
                          }}
                          data-testid={`fx-cell-${gw}-${currency}`}
                        >
                          {editing ? (
                            <input
                              autoFocus
                              type="number"
                              step="0.000001"
                              min="0"
                              className="w-28 rounded border border-pw-primary bg-pw-bg px-2 py-1 text-sm focus:outline-none"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => commitEdit(gw, currency)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit(gw, currency);
                                if (e.key === 'Escape') {
                                  setEditKey(null);
                                  setEditValue('');
                                }
                              }}
                              data-testid={`fx-input-${gw}-${currency}`}
                            />
                          ) : (
                            formatRateMicro(row.rate_micro)
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center gap-2 text-xs font-medium',
                          stale ? 'text-red-600 dark:text-red-300' : 'text-pw-text-muted',
                        )}
                      >
                        <span
                          className={cn(
                            'h-2 w-2 rounded-full',
                            stale ? 'bg-red-500' : 'bg-emerald-500',
                          )}
                        />
                        {stale && age.days >= STALE_DAYS
                          ? t('fx_rate.age.daysStale', { days: age.days })
                          : age.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="text-xs font-medium text-red-600 dark:text-red-300 hover:underline"
                        disabled={deleteMutation.isPending || !freshest}
                        onClick={() => {
                          if (!freshest) return;
                          if (!window.confirm(t('fx_rate.confirm.markStale', { currency }))) return;
                          // Mark first non-stale row as stale (DELETE = soft-stale per Block 2 comment).
                          const target = rowsForCurrency.find((r) => !r.is_stale) ?? freshest;
                          deleteMutation.mutate({ gateway: target.gateway, currency });
                        }}
                        data-testid={`fx-stale-${currency}`}
                      >
                        {t('fx_rate.table.markStale')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {/* ---- Add currency modal ---- */}
      <Dialog open={addOpen} onOpenChange={(o) => !o && setAddOpen(false)}>
        <DialogHeader>
          <DialogTitle>{t('fx_rate.modal.title')}</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-3">
          <div className="flex flex-col gap-2">
            <label htmlFor="add-currency" className="text-sm font-medium text-pw-text">
              {t('fx_rate.modal.codeLabel')}
            </label>
            <Input
              id="add-currency"
              value={addCurrency}
              onChange={(e) => setAddCurrency(e.target.value.toUpperCase())}
              maxLength={3}
              placeholder="USD"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="add-gateway" className="text-sm font-medium text-pw-text">
              {t('fx_rate.modal.gatewayLabel')}
            </label>
            <Input
              id="add-gateway"
              value={addGateway}
              onChange={(e) => setAddGateway(e.target.value.toLowerCase())}
              placeholder={t('fx_rate.modal.gatewayPlaceholder')}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="add-rate" className="text-sm font-medium text-pw-text">
              {t('fx_rate.modal.rateLabel')}
            </label>
            <Input
              id="add-rate"
              type="number"
              step="0.000001"
              value={addRate}
              onChange={(e) => setAddRate(e.target.value)}
              placeholder="1.080000"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setAddOpen(false)}>
            {t('fx_rate.modal.cancel')}
          </Button>
          <Button onClick={submitAdd} data-testid="fx-add-submit">
            {t('fx_rate.modal.add')}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
